import React, { useContext, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import ClientContext from "../../../contexts/ClientContext";
import clientApi from "../../../utils/api/client";
import ToggleSmall from "../../Shared/Toggle/ToggleSmall";
import "./_ClientScheduleDownloadTab.scss";

const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);

const DOC_CATEGORIES = [
  {
    key: "include_project_documents",
    label: "Documents",
    sub: [
      { key: "include_bmp_details", label: "Include BMP Details" },
    ],
  },
  {
    key: "include_site_maps",
    label: "Maps",
    sub: [
      { key: 'include_change_log', label: 'Include change log' },
      { key: 'include_blank_maps', label: 'Include blank maps' },
      { key: 'include_archived_maps', label: 'Include archived maps' },
    ],
  },
  {
    key: "include_inspections",
    label: "Inspections",
    sub: [
      { key: "include_finding_locations", label: "Finding locations" },
      { key: "include_finding_images",    label: "Finding images" },
    ],
  },
];

const DEFAULT_FLAGS: Record<string, boolean> = {
  include_project_documents: false,
  include_bmp_details: false,
  include_site_maps: false,
  include_change_log: false,
  include_blank_maps: false,
  include_archived_maps: false,
  include_inspections: false,
  include_finding_locations: false,
  include_finding_images: false,
};

const ALL_FLAG_KEYS = Object.keys(DEFAULT_FLAGS);

type Recipient = { email: string; user_client_id?: number | null; name?: string };

const ClientScheduleDownloadTab: React.FC = () => {
  const { client } = useContext(ClientContext);
  const clientId = client.id;
  const queryClient = useQueryClient();

  const { data: clientUsers = [] } = useQuery<{ id: number; userClientId: number; name: string; email: string }[]>(
    ["clientScheduleDownloadUsers", clientId],
    () => clientApi.scheduleDownload.getClientUsers(clientId),
    { refetchOnWindowFocus: false }
  );

  const [isEnabled, setIsEnabled] = useState(false);
  const [deliveryDay, setDeliveryDay] = useState(15);
  const [deliveryMethod, setDeliveryMethod] = useState<"merge_by_type" | "merge_by_folder">("merge_by_type");
  const [flags, setFlags] = useState<Record<string, boolean>>(DEFAULT_FLAGS);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [dayCapacityError, setDayCapacityError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const configQuery = useQuery(
    ["clientScheduleDownload", clientId],
    () => clientApi.scheduleDownload.getConfig(clientId),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data: any) => {
        if (data.config) {
          setIsEnabled(data.config.is_enabled);
          setDeliveryDay(data.config.delivery_day);
          setDeliveryMethod(data.config.delivery_method ?? "merge_by_type");
          const loaded: Record<string, boolean> = {};
          ALL_FLAG_KEYS.forEach(k => { loaded[k] = data.config[k] ?? false; });
          setFlags(loaded);
          const resolved: Recipient[] = (data.config.recipients ?? []).map((r: any) => {
            const contact = clientUsers.find(c => c.userClientId === r.user_client_id);
            return { email: r.email, user_client_id: r.user_client_id, name: contact?.name };
          });
          setRecipients(resolved);
        }
      },
    }
  );

  const saveMutation = useMutation<any, any, any>(
    (payload) => clientApi.scheduleDownload.saveConfig(clientId, payload),
    {
      onSuccess: () => {
        setDayCapacityError(null);
        toast.success("Schedule saved successfully");
        queryClient.invalidateQueries(["clientScheduleDownload", clientId]);
      },
      onError: (err: any) => {
        const status = err?.response?.status;
        const message = err?.response?.data?.message ?? "Failed to save schedule";
        if (status === 409) setDayCapacityError(message);
        else toast.error(message);
      },
    }
  );

  const stopMutation = useMutation<any, Error, void>(
    () => clientApi.scheduleDownload.stop(clientId),
    {
      onSuccess: () => {
        setIsEnabled(false);
        toast.success("Schedule stopped");
        queryClient.invalidateQueries(["clientScheduleDownload", clientId]);
      },
      onError: () => { toast.error("Failed to stop schedule"); },
    }
  );

  const resendMutation = useMutation<any, Error, number>(
    (historyId) => clientApi.scheduleDownload.resendLink(clientId, historyId),
    {
      onSuccess: () => { toast.success("Download link re-sent"); },
      onError: () => { toast.error("Failed to re-send link"); },
    }
  );

  const setFlag = (key: string, value: boolean) => setFlags(prev => ({ ...prev, [key]: value }));

  const handleSave = () => {
    setDayCapacityError(null);
    const cleanedRecipients = recipients.map(({ email, user_client_id }) => ({ email, user_client_id }));
    saveMutation.mutate({ is_enabled: isEnabled, delivery_day: deliveryDay, delivery_method: deliveryMethod, ...flags, recipients: cleanedRecipients });
  };

  const addRecipient = () => {
    const trimmed = emailInput.trim();
    if (!trimmed || recipients.some(r => r.email === trimmed)) return;
    const contact = clientUsers.find(c => c.email?.toLowerCase() === trimmed.toLowerCase());
    setRecipients(prev => [...prev, {
      email: contact?.email ?? trimmed,
      user_client_id: contact?.userClientId ?? null,
      name: contact?.name,
    }]);
    setEmailInput("");
    setShowDropdown(false);
  };

  const addContactFromDropdown = (contact: { id: number; userClientId: number; name: string; email: string }) => {
    if (recipients.some(r => r.email === contact.email)) return;
    setRecipients(prev => [...prev, { email: contact.email, user_client_id: contact.userClientId, name: contact.name }]);
    setEmailInput("");
    setShowDropdown(false);
  };

  const filteredContacts = emailInput.trim()
    ? clientUsers.filter(c =>
        !recipients.some(r => r.email === c.email) &&
        (c.name?.toLowerCase().includes(emailInput.toLowerCase()) ||
          c.email?.toLowerCase().includes(emailInput.toLowerCase()))
      )
    : [];

  const handleDownload = async (historyId: number, downloadDate: string) => {
    try {
      const url = await clientApi.scheduleDownload.download(clientId, historyId);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = `archive_${downloadDate}.zip`;
      a.click();
    } catch {
      toast.error("Download failed or link has expired");
    }
  };

  const history: any[] = (configQuery.data as any)?.history ?? [];

  return (
    <div className="schedule-download-tab">

      {/* Schedule toggle */}
      <div className="sd-card">
        <h3 className="sd-card__title">Schedule</h3>
        <div className="sd-schedule-row">
          <ToggleSmall
            isChecked={isEnabled}
            handleChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsEnabled(e.target.checked)}
            id="schedule-toggle"
          />
          <span className="sd-schedule-label">Enable scheduled monthly download</span>
          {isEnabled && <span className="sd-active-badge">Scheduled download is <strong>ACTIVE</strong></span>}
          {!isEnabled && configQuery.data?.config && <span className="sd-paused-badge">Scheduled download is <strong>PAUSED</strong></span>}
        </div>

        {isEnabled && (
          <div className="sd-delivery-row">
            <span>Delivered on the</span>
            <select
              className={`sd-day-select${dayCapacityError ? " sd-day-select--error" : ""}`}
              value={deliveryDay}
              onChange={e => { setDeliveryDay(Number(e.target.value)); setDayCapacityError(null); }}
            >
              {DAY_OPTIONS.map(d => (
                <option key={d} value={d}>{d}{d === 1 ? "st" : d === 2 ? "nd" : d === 3 ? "rd" : "th"}</option>
              ))}
            </select>
            <span>of each month</span>
            {dayCapacityError && <span className="sd-day-error">{dayCapacityError}</span>}
          </div>
        )}
      </div>

      {/* Document options + Distribution list */}
      {isEnabled && (
        <div className="sd-options-row">

          {/* Document options */}
          <div className="sd-card sd-card--doc-options">
            <h3 className="sd-card__title">Document options</h3>
            <ul className="sd-category-list">
              {DOC_CATEGORIES.map(cat => (
                <li key={cat.key} className="sd-category-item">
                  <label className="sd-category-label">
                    <input
                      type="checkbox"
                      checked={flags[cat.key] ?? false}
                      onChange={e => setFlag(cat.key, e.target.checked)}
                      className="sd-checkbox"
                    />
                    {cat.label}
                  </label>
                  {cat.sub.length > 0 && (
                    <ul className="sd-sub-list">
                      {cat.sub.map(sub => (
                        <li key={sub.key} className="sd-sub-item">
                          <label className={`sd-sub-label${!flags[cat.key] ? " sd-sub-label--disabled" : ""}`}>
                            <input
                              type="checkbox"
                              checked={flags[sub.key] ?? false}
                              onChange={e => setFlag(sub.key, e.target.checked)}
                              disabled={!flags[cat.key]}
                              className="sd-checkbox"
                            />
                            {sub.label}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            <div className="sd-delivery-method">
              <h4 className="sd-delivery-method__title">Delivery method</h4>
              <label className="sd-radio-label">
                <input
                  type="radio"
                  name="deliveryMethod"
                  value="merge_by_type"
                  checked={deliveryMethod === "merge_by_type"}
                  onChange={() => setDeliveryMethod("merge_by_type")}
                  className="sd-radio"
                />
                <span className="sd-radio-text">
                  <strong>Merge files by type</strong>
                  <span className="sd-radio-desc">One merged PDF per category inside the project folder (e.g. BMP Details, Inspections, Site Maps, Project Documents)</span>
                </span>
              </label>
              <label className="sd-radio-label">
                <input
                  type="radio"
                  name="deliveryMethod"
                  value="merge_by_folder"
                  checked={deliveryMethod === "merge_by_folder"}
                  onChange={() => setDeliveryMethod("merge_by_folder")}
                  className="sd-radio"
                />
                <span className="sd-radio-text">
                  <strong>Merge files by folder</strong>
                  <span className="sd-radio-desc">Subfolders per category with PDFs merged within each folder; project documents split by document type</span>
                </span>
              </label>
            </div>
          </div>

          {/* Distribution list */}
          <div className="sd-card sd-card--distribution">
            <div className="sd-distribution-header">
              <h3 className="sd-card__title">Distribution list</h3>
            </div>
            <div className="sd-email-input-row" style={{ position: "relative" }}>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search or add by email..."
                className="sd-email-input"
                value={emailInput}
                onChange={e => { setEmailInput(e.target.value); setShowDropdown(true); }}
                onKeyDown={e => e.key === "Enter" && addRecipient()}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                onFocus={() => emailInput.trim() && setShowDropdown(true)}
                autoComplete="off"
              />
              {showDropdown && filteredContacts.length > 0 && (
                <ul className="sd-contact-dropdown">
                  {filteredContacts.map(c => (
                    <li key={c.id} className="sd-contact-dropdown__item" onMouseDown={() => addContactFromDropdown(c)}>
                      <span className="sd-contact-dropdown__name">{c.name}</span>
                      <span className="sd-contact-dropdown__email">{c.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="sd-chips">
              {recipients.map(r => (
                <span key={r.email} className="sd-chip">
                  {r.name ?? r.email}
                  <button className="sd-chip__remove" onClick={() => setRecipients(prev => prev.filter(x => x.email !== r.email))}>×</button>
                </span>
              ))}
            </div>
            <p className="sd-distribution-note">
              Recipients receive an email with a 7-day download link when the archive is ready.
            </p>
          </div>
        </div>
      )}

      {/* Save / Stop buttons */}
      {isEnabled && (
        <div className="sd-actions">
          <button className="outline" onClick={() => stopMutation.mutate()} disabled={stopMutation.isLoading}>
            Stop Schedule
          </button>
          <button className="primary" onClick={handleSave} disabled={saveMutation.isLoading}>
            Save Schedule
          </button>
        </div>
      )}

      {/* Download history */}
      <div className="sd-card sd-card--history">
        <h3 className="sd-card__title">View Download History</h3>
        {history.length === 0 ? (
          <p className="sd-no-history">No downloads yet.</p>
        ) : (
          <table className="sd-history-table">
            <thead>
              <tr>
                <th>Date of download</th>
                <th>Number of projects</th>
                <th>Interval</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row: any) => (
                <tr key={row.id}>
                  <td>{new Date(row.download_date).toLocaleDateString("en-US")}</td>
                  <td>{row.number_of_projects}</td>
                  <td>{row.interval}</td>
                  <td className="sd-history-actions">
                    <button className="outline" onClick={() => resendMutation.mutate(row.id)} disabled={resendMutation.isLoading}>
                      Re-Send Link
                    </button>
                    <button className="primary" onClick={() => handleDownload(row.id, new Date(row.download_date).toISOString().split("T")[0])}>
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ClientScheduleDownloadTab;
